import { useEffect } from 'react';

/**
 * Déclenche le téléchargement de tous les chunks de route en arrière-plan,
 * pendant les temps morts du navigateur (requestIdleCallback).
 *
 * Résultat : dès que l'utilisateur clique sur un item de la sidebar,
 * le chunk est déjà dans le cache du navigateur → navigation instantanée,
 * aucun spinner visible.
 *
 * Ce composant ne rend rien — il s'utilise comme :
 *   <RoutePreloader />
 * dans DashboardLayout, après l'authentification.
 */

// Liste exhaustive de tous les imports de routes (miroir de routes.js)
const ROUTE_CHUNKS = [
  () => import('../pages/dashboard/index.js'),
  () => import('../pages/dashboard/customers/index.js'),
  () => import('../pages/dashboard/audits/index.js'),
  () => import('../pages/dashboard/audits/[auditId].js'),
  () => import('../pages/dashboard/agenda/index.js'),
  () => import('../pages/dashboard/tcs/index.js'),
  () => import('../pages/dashboard/tcs/[tcs].js'),
  () => import('../pages/dashboard/errors.js'),
  () => import('../pages/dashboard/create/index.js'),
  () => import('../pages/dashboard/create/index-admin.js'),
  () => import('../pages/dashboard/customers/[customerId].js'),
  () => import('../pages/dashboard/session/[session].js'),
  () => import('../pages/dashboard/session/create_session.js'),
  () => import('../pages/dashboard/session/home.js'),
  () => import('../pages/dashboard/forum/index.js'),
  () => import('../pages/dashboard/formations/index.js'),
  () => import('../pages/dashboard/formations/[formationId].js'),
  () => import('../pages/dashboard/facturation/index.js'),
  () => import('../pages/dashboard/activities/index.js'),
  () => import('../pages/dashboard/parameters/index.js'),
  () => import('../pages/dashboard/FeedBacks/index.js'),
  () => import('../pages/dashboard/FeedBacks/feedBackByCustomerId.js'),
  () => import('../pages/dashboard/patients_folder/index.js'),
  () => import('../pages/dashboard/patients_folder/[patients_folder].js'),
  () => import('../pages/dashboard/qualite/Seatisfaction.js'),
  () => import('../pages/dashboard/qualite/[surveyId].js'),
  () => import('../pages/dashboard/formation-questionnaire/index.js'),
  () => import('../pages/dashboard/discussion/index.js'),
  () => import('../pages/dashboard/statistiques_audit/index.js'),
  () => import('../pages/dashboard/formateurs/index.js'),
  () => import('../pages/dashboard/formateurs/[formateur].js'),
  () => import('../pages/dashboard/Reglages/Permission_Role/PermissionsTree.js'),
  () => import('../pages/dashboard/Reglages/Permissions_Edit/PermissionsTreee.js'),
  () => import('../pages/dashboard/Reglages/Role/Role_table.js'),
  () => import('../pages/dashboard/import/Imports.js'),
  () => import('../pages/dashboard/Groupes/groupes.js'),
  () => import('../pages/dashboard/Groupes/GroupDetailsPage.js'),
  () => import('../components/dashboard/create/list-admin.js'),
  () => import('../components/dashboard/create/adminUpdateForm.js'),
  () => import('../components/dashboard/scores/Score.js'),
  () => import('../modules/roles_redirection/agenda-role-redirection.js'),
  () => import('../modules/roles_redirection/customers-role-redirection.js'),
  () => import('../modules/roles_redirection/customer-details-role-redirection.js'),
  () => import('../pages/NotAvailable.js'),
  () => import('../pages/NotFound.js'),
];

const RoutePreloader = () => {
  useEffect(() => {
    let idleId;

    const preload = () => {
      // Précharge les chunks un par un avec un délai entre chaque
      // pour ne pas saturer la bande passante au démarrage
      ROUTE_CHUNKS.forEach((importFn, index) => {
        setTimeout(() => {
          importFn().catch(() => {
            // Erreur silencieuse : le chunk sera chargé à la demande si nécessaire
          });
        }, index * 50); // 50ms entre chaque chunk
      });
    };

    if ('requestIdleCallback' in window) {
      // Attend que le navigateur soit inactif (après le rendu initial)
      idleId = window.requestIdleCallback(preload, { timeout: 3000 });
    } else {
      // Fallback : attend 1 seconde après le montage
      idleId = setTimeout(preload, 1000);
    }

    return () => {
      if ('requestIdleCallback' in window) {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, []);

  return null;
};

export default RoutePreloader;
